import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useDemoState } from "@/data/demoState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  HardHat, MapPin, Clock, Users, CheckCircle2, AlertTriangle, FileText,
  Camera, Shield, Phone, ArrowLeft, Smartphone, Upload, XCircle,
  ClipboardCheck, FileWarning, Wrench, ChevronRight
} from "lucide-react";

function MobileShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-100 flex items-start justify-center py-8 px-4">
      <div className="w-full max-w-sm">
        {/* Phone frame */}
        <div className="bg-slate-900 rounded-t-[2rem] px-4 pt-3 pb-2 flex items-center justify-between">
          <span className="text-[10px] text-white font-medium">9:41</span>
          <div className="w-20 h-5 bg-slate-800 rounded-full" />
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm border border-white/30" />
            <div className="w-1 h-3 bg-white/30 rounded-full" />
          </div>
        </div>
        <div className="bg-white border-x-2 border-slate-900 overflow-y-auto" style={{ maxHeight: '72vh' }}>
          {children}
        </div>
        <div className="bg-slate-900 rounded-b-[2rem] px-4 py-3 flex justify-center">
          <div className="w-32 h-1 bg-slate-600 rounded-full" />
        </div>
      </div>
    </div>
  );
}

function TradePackSection({ trade, conditions, project }) {
  const relevantConditions = conditions.filter(c => c.projectId === trade.projectId && c.stage === trade.stage);
  return (
    <div className="space-y-3">
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
        <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <FileText className="w-3 h-3" /> Site Notes
        </p>
        <p className="text-xs text-blue-700">Standard PPE required. Delivery access via rear gate. Report to site office on arrival. No hot works without permit.</p>
      </div>
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
        <p className="text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <ClipboardCheck className="w-3 h-3" /> Relevant Conditions ({relevantConditions.length})
        </p>
        <div className="space-y-1.5">
          {relevantConditions.slice(0, 4).map(c => (
            <div key={c.id} className="flex items-center gap-2 text-[11px]">
              {c.status === 'cleared' ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" /> : <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />}
              <span className={`flex-1 ${c.status === 'cleared' ? 'text-slate-500' : 'text-slate-700 font-medium'}`}>{c.number} {c.title}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
        <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
          <Shield className="w-3 h-3" /> Safety Reminder
        </p>
        <p className="text-[11px] text-amber-700">All workers must have valid site induction. Wear hi-vis, hard hat, and steel-cap boots at all times.</p>
      </div>
    </div>
  );
}

export default function TradePass() {
  const { tradeId } = useParams();
  const { trades, conditions, projects, checkInTrade, markCannotStart, resolveCannotStart, submitWorkProof } = useDemoState();
  const [crewInput, setCrewInput] = useState(3);
  const [cannotStartInput, setCannotStartInput] = useState('');
  const [showCannotStart, setShowCannotStart] = useState(false);
  const [showWorkProof, setShowWorkProof] = useState(false);
  const [activeTab, setActiveTab] = useState('pack');

  const trade = useMemo(() => trades.find(t => t.id === tradeId), [trades, tradeId]);
  const project = useMemo(() => trade ? projects.find(p => p.id === trade.projectId) : null, [trade, projects]);

  if (!trade) {
    return (
      <MobileShell>
        <div className="p-6 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <p className="text-sm font-bold text-slate-800">Trade Pass Not Found</p>
          <p className="text-xs text-slate-500 mt-1">This link may have expired or is invalid.</p>
          <Link to="/builder/trade-release"><Button size="sm" className="mt-4 text-xs"><ArrowLeft className="w-3 h-3 mr-1" /> Back to Trade Release</Button></Link>
        </div>
      </MobileShell>
    );
  }

  const isCheckedIn = trade.status === 'checked-in' || trade.passStatus === 'checked-in';
  const isCannotStart = trade.status === 'cannot-start';
  const isSignedOff = trade.status === 'signed-off';

  const handleCheckIn = () => {
    checkInTrade(trade.id, crewInput);
    toast.success(`Checked in with ${crewInput} crew`, { description: 'You are now on site.' });
  };

  const handleCannotStart = () => {
    if (!cannotStartInput.trim()) return;
    markCannotStart(trade.id, cannotStartInput);
    toast.error('Cannot Start reported', { description: 'Supervisor has been notified.' });
    setShowCannotStart(false);
    setCannotStartInput('');
  };

  const handleWorkProof = () => {
    submitWorkProof(trade.id, 'WorkProof_Photo.jpg', 'Work completed as per specification');
    toast.success('Work proof uploaded', { description: 'Awaiting supervisor review.' });
    setShowWorkProof(false);
  };

  const tabs = [
    { id: 'pack', label: 'Trade Pack', icon: FileText },
    { id: 'checkin', label: isCheckedIn ? 'On Site' : 'Check In', icon: isCheckedIn ? CheckCircle2 : Users },
    { id: 'proof', label: 'Work Proof', icon: Camera },
  ];

  return (
    <MobileShell>
      <div data-testid="trade-pass-view">
        {/* Header */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-800 px-4 py-4">
          <div className="flex items-center gap-1.5 mb-3">
            <Smartphone className="w-3 h-3 text-blue-400" />
            <span className="text-[9px] text-blue-400 font-bold uppercase tracking-widest">SiteReady Trade Pass</span>
          </div>
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
              <HardHat className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">{trade.trade}</p>
              <p className="text-[10px] text-slate-400">{trade.company}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{project?.address || trade.project}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={`text-[9px] ${isCheckedIn ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : isCannotStart ? 'bg-red-500/20 text-red-300 border-red-500/30' : isSignedOff ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-amber-500/20 text-amber-300 border-amber-500/30'}`}>
              {isCheckedIn ? 'On Site' : isCannotStart ? 'Cannot Start' : isSignedOff ? 'Signed Off' : 'Ready'}
            </Badge>
            {trade.crewCount && <Badge className="text-[9px] bg-white/10 text-white border-white/20"><Users className="w-2.5 h-2.5 mr-1" />{trade.crewCount} crew</Badge>}
            {trade.checkInTime && <Badge className="text-[9px] bg-white/10 text-white border-white/20"><Clock className="w-2.5 h-2.5 mr-1" />{trade.checkInTime}</Badge>}
          </div>
        </div>

        {/* Tab Nav */}
        <div className="flex border-b border-slate-200">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={`flex-1 py-2.5 text-center text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-colors ${activeTab === tab.id ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-400'}`}>
              <tab.icon className="w-3 h-3" />{tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'pack' && (
            <TradePackSection trade={trade} conditions={conditions} project={project} />
          )}

          {activeTab === 'checkin' && (
            <div className="space-y-4">
              {!isCheckedIn && !isCannotStart && !isSignedOff ? (
                <>
                  <div className="text-center py-2">
                    <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm font-bold text-slate-800">Check In to Site</p>
                    <p className="text-xs text-slate-500 mt-1">Confirm your crew count to begin</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <label className="text-[10px] font-bold text-slate-600 uppercase">Crew on site</label>
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => setCrewInput(Math.max(1, crewInput - 1))} className="w-10 h-10 rounded-lg bg-white border border-slate-200 text-lg font-bold text-slate-600 hover:bg-slate-100">-</button>
                      <span className="text-2xl font-bold text-slate-800 w-12 text-center">{crewInput}</span>
                      <button onClick={() => setCrewInput(crewInput + 1)} className="w-10 h-10 rounded-lg bg-white border border-slate-200 text-lg font-bold text-slate-600 hover:bg-slate-100">+</button>
                    </div>
                  </div>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={handleCheckIn} data-testid="checkin-btn">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Check In Now
                  </Button>
                  <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50 font-medium text-xs" onClick={() => setShowCannotStart(true)} data-testid="cannot-start-toggle">
                    <XCircle className="w-3.5 h-3.5 mr-1.5" /> Report Cannot Start
                  </Button>
                </>
              ) : isCannotStart ? (
                <div className="space-y-3">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-sm font-bold text-red-800">Cannot Start Reported</p>
                    <p className="text-xs text-red-600 mt-1">{trade.cannotStartReason}</p>
                    <p className="text-[10px] text-red-500 mt-2">Supervisor has been notified</p>
                  </div>
                </div>
              ) : isSignedOff ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-sm font-bold text-blue-800">Trade Signed Off</p>
                  <p className="text-xs text-blue-600 mt-1">Signed off by {trade.signedOffBy}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                    <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm font-bold text-emerald-800">Checked In</p>
                    <p className="text-xs text-emerald-600">{trade.crewCount} crew · Since {trade.checkInTime}</p>
                  </div>
                  <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50 text-xs" onClick={() => setShowCannotStart(true)}>
                    <XCircle className="w-3.5 h-3.5 mr-1.5" /> Report Cannot Start
                  </Button>
                </div>
              )}

              {/* Cannot Start Form */}
              {showCannotStart && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3 mt-2">
                  <p className="text-xs font-bold text-red-800">What is preventing you from starting?</p>
                  <textarea value={cannotStartInput} onChange={e => setCannotStartInput(e.target.value)} rows={3} placeholder="e.g. Waterproofing membrane spec not approved yet..."
                    className="w-full text-xs border border-red-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-red-300" data-testid="cannot-start-reason" />
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs" onClick={handleCannotStart} data-testid="submit-cannot-start">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Submit
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowCannotStart(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'proof' && (
            <div className="space-y-4">
              {!showWorkProof ? (
                <>
                  <div className="text-center py-2">
                    <Camera className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm font-bold text-slate-800">Work Proof</p>
                    <p className="text-xs text-slate-500 mt-1">Upload photos of completed work for supervisor sign-off</p>
                  </div>
                  {(trade.workProofs || []).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-slate-600 uppercase">Uploaded</p>
                      {trade.workProofs.map((wpId, i) => (
                        <div key={wpId} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-700 flex-1">Work proof {i + 1}</span>
                          <Badge className="text-[8px] bg-emerald-50 text-emerald-700">Uploaded</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button className="w-full bg-slate-800 hover:bg-slate-900 text-white" onClick={() => setShowWorkProof(true)} data-testid="upload-work-proof-btn">
                    <Upload className="w-4 h-4 mr-2" /> Upload Work Proof
                  </Button>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50">
                    <Camera className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">Tap to take photo or select from gallery</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={handleWorkProof} data-testid="confirm-work-proof">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Submit Proof
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowWorkProof(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom Link */}
        <div className="px-4 pb-4">
          <Link to="/builder/trade-release">
            <Button variant="ghost" size="sm" className="w-full text-[10px] text-slate-400 h-7">
              <ArrowLeft className="w-3 h-3 mr-1" /> Back to Trade Release
            </Button>
          </Link>
        </div>
      </div>
    </MobileShell>
  );
}
